import { useState, useEffect, useMemo } from "react";
import { useProfile } from "@/hooks/useProfiles";
import AppBreadcrumbs from "@/components/AppBreadcrumbs";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useRouteRestore } from "@/hooks/useRouteRestore";
import { motion, AnimatePresence } from "framer-motion";
import {
  Ticket,
  Mail,
  LayoutDashboard,
  Users,
  Brain,
  GraduationCap,
  BookOpen,
  ShieldAlert,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Menu,
  Moon,
  Sun,
  Crown,
  UserCheck,
  Layers,
  Award,
  Route,
  Network,
  Activity,
  TrendingUp,
  Building2,
  CalendarDays,
  Handshake,
  BarChart3,
  Compass,
  AlertTriangle,
  Target,
  ShieldCheck,
  Wrench,
  CreditCard,
  Receipt,
  RefreshCw,
  Headset,
  MessageSquare,
  Globe,
  Users2,
  UserCog,
  Rocket,
  ClipboardList,
  Trophy,
  Kanban,
  LayoutTemplate,
  HelpCircle,
  ArrowRightLeft,
  Video,
  FlaskConical,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useActiveContext } from "@/hooks/useActiveContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigationVisibility } from "@/hooks/useNavigationVisibility";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import NotificationBell from "@/components/NotificationBell";
import OrgOverrideBanner from "@/components/OrgOverrideBanner";
import MemberViewBanner from "@/components/MemberViewBanner";
import TrialDemoBanner from "@/components/trial/TrialDemoBanner";
import { useTerminology } from "@/hooks/useTerminology";
import OrgSwitcher from "@/components/OrgSwitcher";
import WorkspaceSwitcher from "@/components/workspaces/WorkspaceSwitcher";
import MemberOSLogoWhite from "@/assets/MemberOS-Logo-White.png";
import { useSidebarBadges } from "@/hooks/useSidebarBadges";
import { getRoleNav } from "@/config/roleNavConfigs";
import GlobalSearch from "@/components/GlobalSearch";
import WalkthroughOverlay from "@/components/walkthroughs/WalkthroughOverlay";
import HelpPanel from "@/components/walkthroughs/HelpPanel";
import { useWalkthrough } from "@/hooks/walkthroughs";

/* ─── Navigation Architecture ─── */

interface NavChild {
  to: string;
  label: string;
  icon: React.ElementType;
  /** Module key — hide child if module is inactive */
  module?: string;
}

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  children?: NavChild[];
  adminOnly?: boolean;
  /** Which workspace role emphasizes this nav item */
  workspace?: string;
  /** Module key — hide if module is inactive */
  module?: string;
}

const getNavItems = (teamLabel: string): NavItem[] => [
  { to: "/launch", icon: Rocket, label: "Launch MemberOS" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", workspace: "executive_director" },
  {
    to: "/member-success",
    icon: Users,
    label: "Member Success",
    workspace: "membership_pro",
    module: "membership",
    children: [
      { to: "/member-success", icon: Users, label: "Overview", module: "membership" },
      { to: "/crm", icon: Headset, label: "Prospects" },
      { to: "/members", icon: Users, label: "Members", module: "membership" },
      { to: "/companies", icon: Building2, label: "Companies", module: "membership" },
      { to: "/staff", icon: UserCog, label: "Staff" },
    ],
  },
  {
    to: "/programs",
    icon: GraduationCap,
    label: "Programming",
    workspace: "event_planner",
    children: [
      { to: "/programming-calendar", icon: Kanban, label: "Command Center", module: "programming_calendar" },
      { to: "/events", icon: CalendarDays, label: "Events", module: "events" },
      { to: "/signature-events", icon: Trophy, label: "Signature Events", module: "events" },
      { to: "/playbooks", icon: LayoutTemplate, label: "Playbooks", module: "programming_calendar" },
      { to: "/programs", icon: GraduationCap, label: "Programs", module: "programs" },
      { to: "/learning", icon: BookOpen, label: "Learning", module: "programs" },
      { to: "/video-library", icon: Video, label: "Video Library", module: "video_library" },
      { to: "/learning-hub", icon: GraduationCap, label: "Learning Hub", module: "video_library" },
      { to: "/meeting-ops", icon: ClipboardList, label: "Meeting Ops" },
    ],
  },
  {
    to: "/communications",
    icon: Mail,
    label: "Communications",
    workspace: "marketing_comms",
    module: "communications_pro",
  },
  { to: "/community", icon: MessageSquare, label: "Community", workspace: "marketing_comms", module: "community" },
  { to: "/committees", icon: Users2, label: teamLabel },
  {
    to: "/intelligence",
    icon: Brain,
    label: "Intelligence",
    workspace: "executive_director",
    module: "executive_intelligence",
  },
  {
    to: "/member-success?tab=renewals",
    icon: ShieldAlert,
    label: "Retention",
    workspace: "membership_pro",
    module: "membership",
  },
  {
    to: "/billing",
    icon: CreditCard,
    label: "Revenue",
    workspace: "executive_director",
    children: [
      { to: "/member-success?tab=plans", icon: Layers, label: "Plans & Tiers", module: "membership" },
      { to: "/billing", icon: Receipt, label: "Billing" },
      { to: "/sponsorship", icon: Handshake, label: "Sponsorship", module: "sponsors" },
    ],
  },
  {
    to: "/reports",
    icon: FileText,
    label: "Reports",
    workspace: "executive_director",
    module: "executive_intelligence",
  },
  {
    to: "/website",
    icon: Globe,
    label: "Sites",
    workspace: "marketing_comms",
    children: [
      { to: "/website", icon: Globe, label: "Website", module: "website_pro" },
      { to: "/funnels", icon: Rocket, label: "Campaign Funnels", module: "campaign_funnels" },
      { to: "/response-studio", icon: ClipboardList, label: "Response Studio", module: "response_studio" },
    ],
  },
  { to: "/exchange", icon: ArrowRightLeft, label: "Exchange" },
  { to: "/support", icon: Headset, label: "Support", module: "support_ops" },
  { to: "/plans-billing", icon: Crown, label: "Plans & Billing", adminOnly: true },
  { to: "/settings", icon: Wrench, label: "Settings" },
];

/** Platform-level nav for Master Admins when NOT impersonating an org */
const getMasterAdminNavItems = (): NavItem[] => [
  // ── 1) Platform Operations ──
  { to: "/master-admin", icon: Activity, label: "Platform Overview" },
  {
    to: "/master-admin/ops",
    icon: Building2,
    label: "Platform Operations",
    children: [
      { to: "/master-admin/associations", icon: Network, label: "Associations" },
      { to: "/master-admin/organizations", icon: Building2, label: "Organizations" },
      { to: "/master-admin/trials", icon: FlaskConical, label: "Trials" },
      { to: "/master-admin/provisioning", icon: Rocket, label: "Provisioning" },
      { to: "/master-admin/hierarchy", icon: Layers, label: "Hierarchy Map" },
      { to: "/master-admin/exchange", icon: ArrowRightLeft, label: "Exchange" },
    ],
  },
  // ── 2) Commercial Operations ──
  {
    to: "/master-admin/billing",
    icon: CreditCard,
    label: "Commercial Ops",
    children: [
      { to: "/master-admin/billing", icon: CreditCard, label: "Billing Command Center" },
      { to: "/master-admin/billing/packaging", icon: Layers, label: "Plans & Packaging" },
      { to: "/master-admin/billing/subscriptions", icon: Receipt, label: "Subscriptions" },
      { to: "/master-admin/billing/invoices", icon: Receipt, label: "Invoices" },
      { to: "/master-admin/billing/entitlements", icon: ShieldCheck, label: "Entitlements" },
      { to: "/master-admin/billing/exceptions", icon: AlertTriangle, label: "Overrides & Exceptions" },
      { to: "/master-admin/billing/resolver", icon: Target, label: "Resolver Inspector" },
      { to: "/master-admin/billing/proposals", icon: FileText, label: "Sales Proposals" },
    ],
  },
  // ── 3) Identity & Access ──
  {
    to: "/master-admin/access",
    icon: ShieldCheck,
    label: "Identity & Access",
    children: [
      { to: "/master-admin/access/roles", icon: Crown, label: "Platform Roles" },
      { to: "/master-admin/access/invitations", icon: Users, label: "Invitations" },
      { to: "/master-admin/access/permissions", icon: ShieldAlert, label: "Permission Matrix" },
      { to: "/master-admin/access/logs", icon: FileText, label: "Access Logs" },
    ],
  },
  // ── 4) Platform Intelligence ──
  {
    to: "/master-admin/intelligence",
    icon: BarChart3,
    label: "Intelligence",
    children: [
      { to: "/master-admin/intelligence/reports", icon: FileText, label: "Reports" },
      { to: "/master-admin/intelligence/analytics", icon: Brain, label: "Analytics" },
      { to: "/master-admin/intelligence/adoption", icon: TrendingUp, label: "Adoption" },
      { to: "/master-admin/intelligence/engagement", icon: Activity, label: "Engagement" },
      { to: "/master-admin/intelligence/renewal", icon: RefreshCw, label: "Renewal Risk" },
      { to: "/master-admin/intelligence/revenue", icon: CreditCard, label: "Revenue Health" },
    ],
  },
  // ── 5) Support & Reliability ──
  {
    to: "/master-admin/reliability",
    icon: Headset,
    label: "Support & Reliability",
    children: [
      { to: "/master-admin/reliability/support", icon: Headset, label: "Attention Queue" },
      { to: "/master-admin/reliability/provisioning", icon: AlertTriangle, label: "Provisioning Failures" },
      { to: "/master-admin/reliability/webhooks", icon: Globe, label: "Webhook Monitor" },
      { to: "/master-admin/reliability/sync", icon: RefreshCw, label: "Sync Health" },
      { to: "/master-admin/reliability/alerts", icon: AlertTriangle, label: "Alerts & Incidents" },
    ],
  },
  // ── 6) Governance & Configuration ──
  {
    to: "/master-admin/governance",
    icon: Settings,
    label: "Governance",
    children: [
      { to: "/master-admin/governance/flags", icon: Compass, label: "Feature Flags" },
      { to: "/master-admin/governance/audit", icon: FileText, label: "Audit Log" },
      { to: "/master-admin/governance/lifecycle", icon: Route, label: "Lifecycle Rules" },
      { to: "/master-admin/governance/scoring", icon: Target, label: "Scoring Engine" },
      { to: "/master-admin/governance/automation", icon: Kanban, label: "CRM Automation" },
    ],
  },
];

/** Association admin nav — shown when association admin is NOT impersonating an org */
const getAssociationAdminNavItems = (): NavItem[] => [
  { to: "/association", icon: Network, label: "Association Hub" },
  { to: "/support", icon: Headset, label: "Support" },
  { to: "/settings", icon: Wrench, label: "Settings" },
];

/* ─── Component ─── */

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  useRouteRestore();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== "undefined") return localStorage.getItem("theme") === "dark";
    return false;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user, signOut } = useAuth();
  const { isAdmin, isMasterAdmin, isAssociationAdmin, role } = useIsAdmin();
  const { activeWorkspace } = useActiveContext();
  const { t: term } = useTerminology();
  const { overrideOrgName } = useOrg();
  const { data: badges } = useSidebarBadges();
  const { isModuleVisible } = useNavigationVisibility();
  const { data: userProfile } = useProfile(user?.id);
  const avatarUrl = userProfile?.avatar_url || null;
  const { toggleHelpPanel } = useWalkthrough();

  // Master Admin in platform mode (not impersonating) gets platform nav
  // Association Admin in platform mode gets association nav
  const isPlatformMode = (isMasterAdmin || isAssociationAdmin) && !overrideOrgName;

  // Role-specific nav: when a workspace role is active and user is in org mode,
  // show a tailored nav structure instead of the full default nav.
  const navItems = useMemo(() => {
    if (isPlatformMode) {
      return isMasterAdmin ? getMasterAdminNavItems() : getAssociationAdminNavItems();
    }
    // Admins always get full nav (they need access to everything)
    if (isAdmin) return getNavItems(term("committees"));
    // Non-admin users with an active workspace get role-specific nav
    const roleNav = getRoleNav(activeWorkspace, term("committees"));
    if (roleNav) return roleNav as NavItem[];
    return getNavItems(term("committees"));
  }, [isPlatformMode, isMasterAdmin, isAdmin, activeWorkspace, term]);

  const displayName = user?.user_metadata?.display_name || user?.email?.split("@")[0] || "User";
  const initials = displayName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const roleBadge =
    isPlatformMode && isMasterAdmin
      ? "Platform Admin"
      : isPlatformMode && isAssociationAdmin
        ? "Association Admin"
        : isMasterAdmin && overrideOrgName
          ? `Viewing: ${overrideOrgName}`
          : isAssociationAdmin && overrideOrgName
            ? `Viewing: ${overrideOrgName}`
            : role === "admin"
              ? "Org Admin"
              : role === "moderator"
                ? "Moderator"
                : "Member";

  // Auto-expand the group that contains the active route
  // biome-ignore lint/correctness/useExhaustiveDependencies: navItems is stable within a render
  useEffect(() => {
    const path = location.pathname;
    const newExpanded: Record<string, boolean> = {};
    navItems.forEach((item) => {
      if (item.children) {
        const isActive = item.to === "/" ? path === "/" : path.startsWith(item.to);
        // Also check children routes
        const childActive = item.children.some(
          (c) => path.startsWith(c.to.split("?")[0]) && c.to.split("?")[0] !== "/",
        );
        if (isActive || childActive) newExpanded[item.label] = true;
      }
    });
    setExpandedGroups((prev) => ({ ...prev, ...newExpanded }));
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally watching pathname only
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  /* ─── Sidebar ─── */
  const showExpanded = isMobile || !collapsed;

  const sidebarContent = (
    <div className="flex flex-col h-full min-h-0">
      {/* Logo */}
      <div className="flex items-center px-4 h-14 border-b border-border shrink-0">
        {showExpanded ? (
          <img src={MemberOSLogoWhite} alt="MemberOS" className="h-11" />
        ) : (
          <img
            src={MemberOSLogoWhite}
            alt="MemberOS"
            className="h-11 object-contain object-left"
            style={{ maxWidth: "28px", overflow: "hidden" }}
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {navItems
          .filter((item) => {
            if (item.adminOnly && !isAdmin) return false;
            // Module gating: hide nav items for inactive modules (skip in platform mode)
            if (!isPlatformMode && item.module && !isModuleVisible(item.module)) return false;
            return true;
          })
          .map((item) => {
            const basePath = item.to.split("?")[0];
            const hasChildren = item.children && item.children.length > 0;
            // Simple items use exact match; groups use prefix match + child check
            const childActive = hasChildren && item.children!.some(
              (c) => location.pathname.startsWith(c.to.split("?")[0]) && c.to.split("?")[0] !== "/"
            );
            const isActive = basePath === "/"
              ? location.pathname === "/"
              : hasChildren
                ? (location.pathname.startsWith(basePath) || childActive)
                : location.pathname === basePath;
            const isExpanded = expandedGroups[item.label];
            const isWorkspaceEmphasis = !isPlatformMode && !!activeWorkspace && item.workspace === activeWorkspace;

            // Filter children by admin access and module gating
            const visibleChildren = item.children?.filter((child) => {
              if (child.to === "/admin" && !isAdmin) return false;
              if (!isPlatformMode && child.module && !isModuleVisible(child.module)) return false;
              return true;
            });

            if (!hasChildren) {
              // Simple nav link
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] transition-all relative group ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : isWorkspaceEmphasis
                        ? "text-sidebar-foreground font-medium hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="nav-active"
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-4 rounded-full bg-primary"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  <item.icon className="w-4 h-4 shrink-0" />
                  {showExpanded && <span className="flex-1 whitespace-nowrap overflow-hidden">{item.label}</span>}
                  {isWorkspaceEmphasis && !isActive && showExpanded && (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/40 shrink-0" />
                  )}
                  {badges?.[item.to] && badges[item.to] > 0 && (
                    <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1">
                      {badges[item.to] > 99 ? "99+" : badges[item.to]}
                    </span>
                  )}
                </NavLink>
              );
            }

            // Collapsible group
            return (
              <div key={item.label} className="space-y-0.5">
                <button
                  onClick={() => {
                    if (showExpanded) {
                      toggleGroup(item.label);
                    } else {
                      navigate(item.to);
                    }
                  }}
                  className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-[13px] transition-all relative group ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : isWorkspaceEmphasis
                        ? "text-sidebar-foreground font-medium hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="nav-active"
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-4 rounded-full bg-primary"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  <item.icon className="w-4 h-4 shrink-0" />
                  {showExpanded && (
                    <>
                      <span className="flex-1 text-left whitespace-nowrap overflow-hidden">{item.label}</span>
                      {(() => {
                        const groupTotal = visibleChildren?.reduce((sum, c) => sum + (badges?.[c.to] || 0), 0) || 0;
                        return groupTotal > 0 ? (
                          <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1 mr-1">
                            {groupTotal > 99 ? "99+" : groupTotal}
                          </span>
                        ) : null;
                      })()}
                      <ChevronDown
                        className={`w-3 h-3 shrink-0 text-muted-foreground transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </>
                  )}
                </button>

                {/* Sub-navigation */}
                {showExpanded && (
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="ml-4 pl-2.5 border-l border-border/50 space-y-0.5 py-0.5">
                          {visibleChildren?.map((child) => {
                            const childBasePath = child.to.split("?")[0];
                            const childActive =
                              location.pathname === childBasePath &&
                              (child.to.includes("?")
                                ? location.search.includes(child.to.split("?")[1])
                                : !location.search);

                            return (
                              <NavLink
                                key={child.to}
                                to={child.to}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] transition-all ${
                                  childActive
                                    ? "text-primary font-medium bg-primary/5"
                                    : "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent"
                                }`}
                              >
                                <child.icon className="w-3.5 h-3.5 shrink-0" />
                                <span className="flex-1 whitespace-nowrap overflow-hidden">{child.label}</span>
                                {badges?.[child.to] && badges[child.to] > 0 && (
                                  <span className="ml-auto min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold px-0.5">
                                    {badges[child.to] > 99 ? "99+" : badges[child.to]}
                                  </span>
                                )}
                              </NavLink>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })}
      </nav>

      {/* Profile + Actions */}
      <div className="border-t border-border shrink-0">
        <button
          onClick={() => navigate(isMasterAdmin ? "/master-admin" : "/profile")}
          className="flex items-center gap-2.5 w-full px-3 py-3 hover:bg-sidebar-accent transition-colors text-left"
        >
          <div className="relative shrink-0">
            {avatarUrl ? (
              <img src={avatarUrl} alt={displayName} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-[11px] font-semibold text-primary">
                {initials}
              </div>
            )}
            {isMasterAdmin && (
              <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-warning flex items-center justify-center">
                <Crown className="w-2 h-2 text-warning-foreground" />
              </div>
            )}
          </div>
          {showExpanded && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-sidebar-accent-foreground truncate">{displayName}</div>
              <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                {isMasterAdmin && <Crown className="w-2.5 h-2.5 text-warning" />}
                {roleBadge}
              </div>
            </div>
          )}
        </button>

        <div className="px-2 pb-2 space-y-0.5">
          <button
            onClick={signOut}
            className="flex items-center justify-center w-full gap-2 px-2.5 py-2 rounded-md text-[13px] text-destructive hover:bg-destructive/10 transition-all"
          >
            <LogOut className="w-4 h-4" />
            {showExpanded && <span>Sign Out</span>}
          </button>
          {!isMobile && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="flex items-center justify-center w-full gap-2 px-2.5 py-2 rounded-md text-[13px] text-sidebar-foreground hover:bg-sidebar-accent transition-all"
            >
              {collapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <>
                  <ChevronLeft className="w-4 h-4" />
                  <span>Collapse</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <motion.aside
          animate={{ width: collapsed ? 64 : 240 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="relative flex flex-col h-full border-r border-border bg-sidebar shrink-0 z-20 overflow-hidden"
        >
          {sidebarContent}
        </motion.aside>
      )}

      {/* Mobile Sheet */}
      {isMobile && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            {sidebarContent}
          </SheetContent>
        </Sheet>
      )}

      {/* Main Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-14 border-b border-border bg-background/80 backdrop-blur-md flex items-center justify-between px-4 md:px-6 shrink-0 z-10">
          <div className="flex items-center gap-3">
            {isMobile && (
              <button
                onClick={() => setMobileOpen(true)}
                className="p-1.5 rounded-md hover:bg-secondary transition-colors"
              >
                <Menu className="w-5 h-5 text-foreground" />
              </button>
            )}
            <GlobalSearch />
          </div>
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-2">
              <WorkspaceSwitcher />
              <OrgSwitcher />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setDarkMode(!darkMode)}
                    className="p-2 rounded-md hover:bg-secondary transition-colors"
                    aria-label="Toggle dark mode"
                    type="button"
                  >
                    {darkMode ? (
                      <Sun className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Moon className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{darkMode ? "Switch to light mode" : "Switch to dark mode"}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={toggleHelpPanel}
                    className="p-2 rounded-md hover:bg-secondary transition-colors"
                    aria-label="Help & guides"
                    type="button"
                  >
                    <HelpCircle className="w-4 h-4 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Help & guides</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span><NotificationBell /></span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Notifications</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={displayName}
                      onClick={() => navigate("/profile")}
                      className="w-7 h-7 rounded-full object-cover cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all"
                    />
                  ) : (
                    <button
                      onClick={() => navigate("/profile")}
                      className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-[10px] font-semibold text-primary hover:bg-primary/25 transition-colors cursor-pointer"
                      type="button"
                    >
                      {initials}
                    </button>
                  )}
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Your profile</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </header>

        <OrgOverrideBanner />
        <MemberViewBanner />
        <TrialDemoBanner />

        <main className="flex-1 overflow-y-auto">
          <div className="min-h-full">
            <div className="px-6 lg:px-8 pt-4">
              <AppBreadcrumbs />
            </div>
            {children}
          </div>
        </main>
      </div>
      <WalkthroughOverlay />
      <HelpPanel />
    </div>
  );
};

export default AppLayout;
